import type { CustomDashAssistantEntityType } from './global-ai-assistant-api';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import {
  generateDashboardHeadless,
  type HeadlessBuildTaxonomy,
  type HeadlessGeneratedPage,
} from './global-ai-generate-headless';
import {
  dashboardStreamEventToProgress,
  routePageGenProgress,
} from './global-ai-page-gen-progress';
import type { GlobalAssistantConversationPayload } from './global-ai-run-page-stream';
import { createToolProgressSink } from './global-ai-tool-progress';
import { toolStepTitle } from './global-ai-tool-step-titles';
import {
  emptyTrace,
  endTrace,
  finishStep,
  type ReasoningTrace,
  setStepThinking,
  startStep,
  titleStep,
} from './reasoning-trace';

/**
 * Run the LAZY, click-time page-gen headlessly WITH the same reasoning-trace UI
 * a turn produces — live thinking under a running step, then a collapsed
 * "Worked for Xs" summary.
 *
 * The turn-runner streams create_custom_page progress into a per-turn
 * `createToolProgressSink` + `reasoning_trace` transcript element. The
 * `offer_page_or_custom` Build chip generates on click INSTEAD (deferred so
 * partial-fit causal offers aren't taxed) and used to surface only a static
 * "Generating the dashboard…" status. This helper gives that click path the
 * full trace by driving a self-contained single-step trace + progress sink
 * around one `generateDashboardHeadless` call.
 *
 * Returns the generated page (null on failure/clarification — the caller
 * falls back to the legacy resume flow exactly as before) plus, on success,
 * the click's ENDED build trace: the landing narration REOPENS it so its
 * "Reading the page data" step joins the same working block instead of
 * opening a second one (one click, one block; see seeded-build-narration.ts).
 */
export async function runHeadlessBuildWithTrace(params: {
  prompt: string;
  entityType: CustomDashAssistantEntityType;
  conversation?: GlobalAssistantConversationPayload;
  /** See generateDashboardHeadless. */
  taxonomy: HeadlessBuildTaxonomy;
  signal?: AbortSignal;
  newId: () => string;
  appendTranscript: (message: GlobalAiTranscriptMessage) => void;
  updateReasoningTrace: (id: string, trace: ReasoningTrace) => void;
  updateAssistantMessageText: (id: string, text: string) => void;
  setGenerationStatus: (value: string | null) => void;
  nowMs: () => number;
}): Promise<{
  page: HeadlessGeneratedPage | null;
  buildTrace?: { traceId: string; trace: ReasoningTrace };
}> {
  const controller = params.signal ? undefined : new AbortController();
  const signal = params.signal ?? (controller as AbortController).signal;

  const traceId = params.newId();
  const stepId = params.newId();
  let trace: ReasoningTrace = emptyTrace(params.nowMs());
  trace = startStep(trace, stepId);
  trace = titleStep(trace, stepId, toolStepTitle('create_custom_page'));
  params.appendTranscript({
    id: traceId,
    role: 'assistant',
    kind: 'reasoning_trace',
    trace,
  });
  params.updateReasoningTrace(traceId, trace);

  const sink = createToolProgressSink({
    userGoal: params.prompt,
    signal,
    newId: params.newId,
    appendTranscript: params.appendTranscript,
    updateAssistantMessageText: params.updateAssistantMessageText,
    setGenerationStatus: params.setGenerationStatus,
    onThinking: (text) => {
      trace = setStepThinking(trace, stepId, text);
      params.updateReasoningTrace(traceId, trace);
    },
  });

  try {
    const generated = await generateDashboardHeadless({
      prompt: params.prompt,
      entityType: params.entityType,
      ...(params.conversation ? { conversation: params.conversation } : {}),
      taxonomy: params.taxonomy,
      onProgress: (event) => {
        const progress = dashboardStreamEventToProgress(event);
        if (progress) {
          sink.emit(routePageGenProgress(progress));
        }
      },
      signal,
    });
    trace = finishStep(trace, stepId, generated ? 'done' : 'error');
    trace = endTrace(trace, params.nowMs());
    params.updateReasoningTrace(traceId, trace);
    return {
      page: generated,
      // Only a successful build proceeds to a landing (and thus a reading
      // phase that could resume this trace); the legacy fallback keeps its
      // ended trace as-is.
      ...(generated ? { buildTrace: { traceId, trace } } : {}),
    };
  } catch (e) {
    trace = finishStep(trace, stepId, 'error');
    trace = endTrace(trace, params.nowMs());
    params.updateReasoningTrace(traceId, trace);
    throw e;
  } finally {
    sink.close();
  }
}
